import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, IconButton, Grid, Paper, TextField, Button, MenuItem,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Snackbar,
  Chip,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import * as Icons from '@material-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import DataTable from '../components/DataTable';
import QuickSearchToolbar from '../components/QuickSearchToolbar';
import { invokeApig } from '../lib/awsLib';
import NeokodeButton from '../components/NeokodeButton';
import { has_access } from '../lib/access';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  section: {
    margin: theme.spacing(3, 0),
    marginBottom: theme.spacing(10),
    width: 'inherit',
  },
  paper: {
    padding: theme.spacing(2),
  },
  breadcrumbContainer: {
    padding: theme.spacing(1),
  },
  table: {
    minWidth: 650,
  },
  addButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  addButton: {
    margin: '0',
  },
  changeButton: {
    backgroundColor: '#2196f3',
    '&:hover': {
      backgroundColor: '#2196f3',
    },
  },
  searchContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '20px',
  },
  searchInput: {
    marginRight: '10px',
  },
  editIcon: {
    color: '#2196f3',
  },
  enableIcon: {
    color: '#00b31f',
  },
  disableIcon: {
    color: '#f44336',
  },
  rootSearch: {
    padding: theme.spacing(0.5, 0.5, 0),
    justifyContent: 'space-between',
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  textFieldSearch: {
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    margin: theme.spacing(1, 0.5, 1.5),
    '& .MuiSvgIcon-root': {
      marginRight: theme.spacing(0.5),
    },
    '& .MuiInput-underline:before': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  buttonsDialogContainer: {
    padding: '10px 25px',
  },
  refreshIcon: {
    color: '#666',
    margin: '0 15px'
  },
  button: {
    margin: '15px',
  },
  confirmUserText: {
    textAlign: 'center',
  },
  green: {
    backgroundColor: '#00b31f',
    color: '#ffffff',
  },
  red: {
    backgroundColor: '#f44336',
    color: '#ffffff',
  },
  yellow: {
    backgroundColor: '#ff9800',
    color: '#ffffff',
  },
}));

function escapeRegExp(value) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function CustomersPage(props) {
  const access = props.params.access;
  const classes = useStyles();
  const [isGlobalError, setIsGlobalError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [customer, setCustomer] = useState({});
  const [isChangeState, setIsChangeState] = useState(false);
  const [message, setMessage] = useState('');
  const [customers, setCustomers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [openToast, setOpenToast] = useState(false);
  const [messageToast, setMessageToast] = useState('');

  useEffect(() => {
    getCustomers();
  }, []);

  const getCustomers = async () => {
    try {
      setIsLoading(true);
      setIsGlobalError(false);
      setCustomers([]);
      setSearchText('');
      try {
        let lastKey = undefined;
        let allCustomers = [];
        do {
          const res = await invokeApig({
            path: "/account/find",
            method: "POST",
            body: {
              "LastEvaluatedKey": lastKey ? lastKey : undefined,
              "limit": 50
            }
          });
          if (res.code === 0) {
            allCustomers = [...allCustomers, ...res.data.owners];
            setCustomers(allCustomers);
            lastKey = res.data.LastEvaluatedKey;
          } else {
            if (res.code === 403) {
              setMessage(res.message);
              setIsGlobalError(true);
              break;
            } else {
              setMessage('Error al obtener los clientes. Intenta nuevamente o comunícate con Soporte');
              setIsGlobalError(true);
              break;
            }
          }
        } while (lastKey);
        setIsLoading(false);
      } catch (e) {
        setIsGlobalError(true);
      }
    } catch (error) {
      console.error('Error in getCustomers:', error);
      setMessage('Error al obtener los clientes. Intenta nuevamente o comunícate con Soporte');
      setIsGlobalError(true);
    }
    setIsLoading(false);
  }

  const changeState = async (customer) => {
    try {
      setIsLoading(true);
      const newStatus = customer.status === 'Activo' ? 'Inactivo' : 'Activo';
      const response = await invokeApig({
        path: `/account/state`,
        method: "POST",
        body: {
          id: customer.id,
          status: newStatus,
        }
      });
      if (response.code !== 0) {
        setMessage(response.message || 'Ocurrió un error al cambiar el estado del cliente');
        setIsError(true);
      } else {
        setMessageToast(`Cliente ${newStatus === 'Activo' ? 'activado' : 'desactivado'} correctamente`);
        setOpenToast(true);
        setOpenDialog(false);
      }
    } catch (error) {
      console.error('Error in changeState:', error);
      setMessage('Ocurrió un error al cambiar el estado. Por favor intenta más tarde o comunícate con Soporte');
      setIsError(true);
    }
    setIsLoading(false);
    getCustomers();
  }

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
  };

  // Campos por los que se busca (los que identifican al cliente).
  const SEARCH_FIELDS = ['id', 'name', 'email', 'contact'];

  const visibleRows = useMemo(() => {
    let list = customers;
    if (statusFilter === 'active') {
      list = list.filter((c) => c.status !== 'Inactivo');
    } else if (statusFilter === 'inactive') {
      list = list.filter((c) => c.status === 'Inactivo');
    }
    const term = searchText.trim();
    if (term) {
      const searchRegex = new RegExp(escapeRegExp(term), 'i');
      list = list.filter((row) =>
        SEARCH_FIELDS.some((field) => searchRegex.test(String(row[field] ?? '')))
      );
    }
    return list;
  }, [customers, searchText, statusFilter]);

  const handleShowChangeState = (customer) => {
    setCustomer(customer);
    setIsChangeState(true);
    setIsError(false);
    setMessage('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  }

  const handleConfirmAction = () => {
    if (isChangeState) {
      changeState(customer);
    }
  }

  const handleCloseToast = () => {
    setOpenToast(false);
  }

  const columns = [
    {
      id: 'id',
      label: 'RUT',
    },
    {
      id: 'name',
      label: 'Nombre',
    },
    {
      id: 'email',
      label: 'Notificación NK',
    },
    {
      id: 'contact',
      label: 'Contacto',
    },
    {
      id: 'creationDate',
      label: 'Fecha de alta',
      type: 'date',
      render: (row) => (
        moment(row.creationDate).utcOffset(row.creationDateZone).format('DD-MM-YYYY HH:mm')
      ),
    },
    {
      id: 'status',
      label: 'Estado',
      sortable: false,
      render: (row) => (
        row.status === 'Activo' ?
        <Chip
          size="small"
          label="Activo"
          className={classes.green}
        />
        :
        <Chip
          size="small"
          label={row.status}
          className={classes.yellow}
        />
      ),
    },
    {
      id: 'registerPayment',
      label: 'Pago automático',
      sortable: false,
      render: (row) => (
        row.registerPayment ?
        <Chip
          size="small"
          label="Pago automático"
          icon={<Icons.Done style={{ color: '#ffffff' }} />}
          className={classes.green}
        />
        :
        <Chip
          size="small"
          label="Pago manual"
          icon={<Icons.HighlightOff style={{ color: '#ffffff' }} />}
          className={classes.red}
        />
      ),
    },
    {
      id: 'actions',
      label: 'Acciones',
      sortable: false,
      render: (row) => {
        const isActive = row.status === 'Activo';
        return (
          has_access(access, 'customer', ['admin']) &&
          <>
            <IconButton component={RouterLink} to={`/customers/edit/${row.id}`} className={classes.editIcon}>
              <Icons.Edit />
            </IconButton>
            <IconButton component={RouterLink} to={`/customers/${row.id}/users`} className={classes.userIcon}>
              <Icons.Group />
            </IconButton>
            <IconButton onClick={() => handleShowChangeState(row)} className={isActive? classes.disableIcon : classes.enableIcon}>
              {
                isActive ?
                <Icons.RemoveCircle />
                :
                <Icons.CheckCircle />
              }
            </IconButton>
          </>
        );
      },
    }
  ];

  const showConfirmDialog = () => {
    return (
      <Dialog open={openDialog} onClose={handleCloseDialog} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">Confirmación</DialogTitle>
        <DialogContent>
          <p className={classes.confirmUserText}><b>Cliente</b>: {customer.name}</p>
          <Alert severity="info">
            <p>{ isChangeState && 'Se va a modificar el estado del cliente.' }</p>
            <p><b>¿Estás seguro que quieres continuar con la acción?</b></p>
          </Alert>
          {
            isError &&
            <Alert severity="error">
              {message}
            </Alert>
          }
        </DialogContent>
        <DialogActions className={classes.buttonsDialogContainer}>
          <Button
            type="button"
            variant="contained"
            className={classes.button}
            onClick={handleCloseDialog}
            disabled={isLoading}
          >
            No, cerra esta ventana
          </Button>
          <Button
            type="button"
            color={"primary"}
            variant="contained"
            className={classes.changeButton}
            onClick={handleConfirmAction}
            disabled={isLoading}
          >
            Sí, confirmo
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderError = () => (
    <>
      <Alert severity="error">
          {message}
      </Alert>
      <NeokodeButton
        type="button"
        variant="contained"
        className={classes.button}
        component={RouterLink}
        to="/"
      >
        Volver al inicio
      </NeokodeButton>
    </>
  );

  return (
    <Grid container spacing={2} className={classes.section} justifyContent="center">
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <Paper className={classes.paper}>
          <Typography variant="h4" gutterBottom>
            Administración de Clientes
          </Typography>

          {
            isGlobalError ?
            renderError()
            :
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <TextField
                  select
                  size="small"
                  variant="outlined"
                  label="Mostrar"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ minWidth: 170 }}
                >
                  <MenuItem value="active">Activos</MenuItem>
                  <MenuItem value="inactive">Inactivos</MenuItem>
                  <MenuItem value="all">Todos</MenuItem>
                </TextField>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <QuickSearchToolbar
                    value={searchText}
                    onChange={(event) => requestSearch(event.target.value)}
                    clearSearch={() => requestSearch('')}
                    onRefresh={() => getCustomers()}
                    addButtonUrl={has_access(access, 'customer', ['admin']) ? '/customers/create' : false}
                    addButtonText={has_access(access, 'customer', ['admin']) ? 'Nuevo Cliente' : false}
                  />
                </div>
              </div>
              <DataTable
                columns={columns}
                rows={visibleRows}
                getRowId={(row) => row.id}
                loading={isLoading}
                emptyMessage="Sin datos para mostrar"
                initialSort={{ orderBy: 'name', order: 'asc' }}
              />
              {showConfirmDialog()}
            </>
          }
        </Paper>
      </Grid>
      <Snackbar open={openToast} autoHideDuration={6000} onClose={handleCloseToast}>
        <Alert onClose={handleCloseToast} severity="success">
          {messageToast}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default CustomersPage;